/* eslint-disable no-undef */
'use client';

import { signOut } from 'next-auth/react';

const BACKEND_URL =
  process.env.NEXT_PUBLIC_BACKEND_URL || 'https://backend.bidsale.eu';

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

function isAuthError(status: number, body: string): boolean {
  if (status !== 403) return false;
  try {
    const data = JSON.parse(body);
    const detail: string = data?.detail || '';
    return (
      detail.includes('token') ||
      detail.includes('Invalid authentication') ||
      detail.includes('authorization code')
    );
  } catch {
    return false;
  }
}

/**
 * Authenticated fetch helper that automatically includes the bearer token
 * @param endpoint - The API endpoint (can be full URL or path starting with /)
 * @param accessToken - Optional access token for authentication
 * @param options - Standard fetch options (method, body, signal, etc.)
 * @returns Promise<Response>
 */
export async function apiFetch(
  endpoint: string,
  accessToken?: string | null,
  options: FetchOptions = {},
): Promise<Response> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${BACKEND_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...options.headers,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok && typeof window !== 'undefined') {
    const cloned = response.clone();
    const text = await cloned.text();
    if (isAuthError(response.status, text)) {
      await signOut({ callbackUrl: '/auth/login' });
    }
  }

  return response;
}

/**
 * Authenticated fetch helper that returns JSON data
 * @param endpoint - The API endpoint (can be full URL or path starting with /)
 * @param accessToken - Optional access token for authentication
 * @param options - Standard fetch options (method, body, signal, etc.)
 * @returns Promise<T> - Parsed JSON response
 */
export async function apiFetchJson<T = unknown>(
  endpoint: string,
  accessToken?: string | null,
  options: FetchOptions = {},
): Promise<T> {
  const response = await apiFetch(endpoint, accessToken, options);

  if (!response.ok) {
    throw new Error(
      `API request failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Build a URL with query parameters
 * @param endpoint - Base endpoint path
 * @param params - Query parameters as key-value pairs
 * @returns Full URL string
 */
export function buildApiUrl(
  endpoint: string,
  params?: Record<string, string | number | undefined | null>,
): string {
  const baseUrl = endpoint.startsWith('http')
    ? endpoint
    : `${BACKEND_URL}${endpoint}`;

  if (!params) return baseUrl;

  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

export { BACKEND_URL };
